import { Component, OnInit, Input, Output, EventEmitter, ViewEncapsulation, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { AlertService, CampaignService, EmailTemplateService, FunnelStepService } from '../../../_services';
import {
  AutoResponderType,
  EmailResponse,
  SMSResponse,
  EmailTemplate,
  EmailTemplateType,
  getFunnelPathVariables,
  AutoResponderEditors,
  Campaign,
} from '../../../_models';
import { Form } from '../../../_forms';
import { FormGroup } from '@angular/forms';
import { tinyMCETTextOnly } from '../../../_helpers';
import * as tinymce from 'tinymce';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { takeUntil } from 'rxjs/operators';

@Component({
  moduleId: module.id.toString(),
  selector: 'autoresponder-response',
  templateUrl: './autoresponder-response.component.html',
  styleUrls: ['./autoresponder-response.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AutoResponderResponseComponent extends Form implements OnInit {
  campaign: Campaign;
  templateType = EmailTemplateType.AutoResponder;
  smsEditorInitCallback = tinyMCETTextOnly;
  selectedEmailTemplate: EmailTemplate;
  isTemplateForAfterHours: boolean = false;
  minutesOptions = [
    { key: 0, value: 0 },
    { key: 15, value: 15 },
    { key: 30, value: 30 },
    { key: 45, value: 45 },
  ];
  smsPathVariables = getFunnelPathVariables();
  messageContent: string;
  afterHoursMessageContent: string;
  showAfterHourControls: boolean = false;
  autoResponderEditors = AutoResponderEditors;
  templatePopupLoading: boolean = false;
  messageEditor = {
    toolbar: '',
    paste_as_text: true,
    min_height: 200,
    max_height: 500,
  };

  afterHoursMessageEditor = {
    toolbar: '',
    paste_as_text: true,
    min_height: 200,
    max_height: 500,
  };

  @Input('response') response: EmailResponse | SMSResponse;
  @Input('type') type: AutoResponderType;
  @Input('templates') emailTemplates: EmailTemplate[];
  @Input('index') index: number;
  @Input('group') responseForm: FormGroup;
  @Output('emailTemplatesChanged') templatesChanged: EventEmitter<any> = new EventEmitter();
  @Input('templatesLoading') templatesLoading: boolean = false;
  @ViewChild('emailTemplatePopupRef', { static: false }) emailTemplatePopupRef: any = null;

  constructor(
    protected router: Router,
    protected location: Location,
    protected alertService: AlertService,
    protected stepService: FunnelStepService,
    protected modalService: NgxSmartModalService,
    protected templateService: EmailTemplateService,
    protected campaignService: CampaignService
  ) {
    super(alertService, router, location);
  }

  ngOnInit() {
    this.form = this.responseForm;
    let existingValue = this.form.value[this.isSMS() ? 'message_after_hours' : 'template_after_hours'];
    this.showAfterHourControls = !!existingValue;
    this.campaign = this.campaignService.getCurrentCampaign();
  }

  isSMS() {
    return this.type == AutoResponderType.SMS;
  }

  isEmail() {
    return this.type == AutoResponderType.Email;
  }

  isTemplateSelected(template_id: number, afterHours: boolean = false): boolean {
    const controlName = afterHours ? 'template_after_hours' : 'template';
    const control = this.form.get(controlName);

    if (control && control.value) {
      return Number(control.value) === Number(template_id);
    }

    return false;
  }

  editEmailTemplate(template: EmailTemplate, afterHours: boolean = false) {
    this.selectedEmailTemplate = template;
    this.isTemplateForAfterHours = afterHours;
    setTimeout(() => {
      this.modalService.getModal('emailTemplateDialog').open();
    });
  }

  newEmailTemplate(afterHours: boolean = false) {
    this.selectedEmailTemplate = null;
    this.isTemplateForAfterHours = afterHours;
    this.modalService.getModal('emailTemplateDialog').open();
  }

  selectEmailTemplate(template: EmailTemplate, afterHours: boolean = false) {
    this.selectedEmailTemplate = template;
    const controlName = afterHours ? 'template_after_hours' : 'template';
    this.form.patchValue({
      [controlName]: template.id,
    });
  }

  unselectEmailTemplate(afterHours: boolean = false) {
    const controlName = afterHours ? 'template_after_hours' : 'template';
    this.form.patchValue({
      [controlName]: null,
    });
    this.selectedEmailTemplate = null;
  }

  updateEmailTemplateInArray(template: EmailTemplate) {
    const index = this.emailTemplates.findIndex((t) => t.id === template.id);
    if (index !== -1) {
      this.emailTemplates[index] = template;
    }
  }

  toggleShowAfterHourControls(event) {
    this.showAfterHourControls = event.target.checked;
    if (!this.showAfterHourControls) {
      let fieldName = this.isSMS() ? 'message_after_hours' : 'template_after_hours';
      let value = {};
      value[fieldName] = null;
      this.form.patchValue(value);
    }
  }

  addMessageVariable(event, editorType: AutoResponderEditors) {
    const editorId = `${editorType}_${this.index}`;
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = event.target.value;

    const editorInstance = tinymce.get(editorId);
    if (editorInstance) {
      editorInstance.insertContent(selectedValue);
    }

    selectElement.selectedIndex = 0;
  }

  onSaveEmailTemplate(saveAsNew = false) {
    if (this.emailTemplatePopupRef) {
      const templateData: EmailTemplate = this.emailTemplatePopupRef.getFormData();

      if (templateData) {
        this.templatePopupLoading = true;

        if (this.selectedEmailTemplate) {
          if (!saveAsNew) {
            this.editTemplate(templateData);
          } else {
            const newTemplateData = { id: null, ...templateData };
            this.createTemplate(newTemplateData);
          }
        } else {
          this.createTemplate(templateData);
        }

        this.modalService.getModal('emailTemplateDialog').close();
      } else {
        this.alertService.error('Please fill in all required fields', true);
      }
    } else {
      this.alertService.error('Unable to save field. Please try again.', true);
    }
  }

  createTemplate(templateData) {
    this.templateService.create(templateData).subscribe(
      (template: EmailTemplate) => {
        this.templatePopupLoading = false;
        this.emailTemplates.push(template);
        this.alertService.success('Email template created successfully', true);
        this.selectEmailTemplate(template, this.isTemplateForAfterHours);
      },
      (error) => {
        this.templatePopupLoading = false;
        this.handleError(error);
      }
    );
  }

  editTemplate(templateData) {
    this.templateService.update(templateData.id, templateData).subscribe(
      (template: EmailTemplate) => {
        this.templatePopupLoading = false;
        this.updateEmailTemplateInArray(template);
        this.alertService.success('Email template updated successfully', true);
        this.selectEmailTemplate(template, this.isTemplateForAfterHours);
      },
      (error) => {
        this.templatePopupLoading = false;
        this.handleError(error);
      }
    );
  }

  testTemplate() {
    if (this.emailTemplatePopupRef) {
      this.templatePopupLoading = true;
      const templateData: EmailTemplate = this.emailTemplatePopupRef.getFormData();

      let params = {
        subject: templateData.subject,
        body: templateData.body,
        campaign: this.campaign.id,
      };

      this.templateService
        .test(params)
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          () => {
            this.templatePopupLoading = false;
            this.alertService.success('Test email sent.');
          },
          (error) => {
            this.templatePopupLoading = false;
            this.handleError(error);
          }
        );
    }
  }
}
